import { PopoverSize, Select } from 'app/shared/components/generic-ui';
import { useRouteParams } from 'app/shared/hooks/useRouteParams';
import {
  CellAlignment,
  CellSize,
} from 'app/shared/components/generic-ui/Table/Table.types';
import { useCallback, useEffect, useState } from 'react';
import { useStyles } from './EntitiesDropdown.styles';
import { CustomTypography } from 'app/shared/components/generic-ui/Typography/Typography';
import { IBatchesCheckbox, NotificationEntityType } from 'types';

import { usePlantCrossShippingGroupApi } from 'app/shared/hooks/usePlantCrossShippingGroupApi';

interface IEntitiesDropdownProps {
  label: string;
  selectedValues?: string[];
  selectedItems?: IBatchesCheckbox[];
  onChangeSelectedEntities: (
    values: string[],
    items?: IBatchesCheckbox[],
  ) => void;
}

export const CrossShippingGroupsDropdown = ({
  label,
  selectedItems,
  selectedValues,
  onChangeSelectedEntities,
}: IEntitiesDropdownProps) => {
  const { classes } = useStyles();
  const [searchQuery, setSearchQuery] = useState('');
  const { organizationId }: { organizationId: string } = useRouteParams();

  const columns = [
    {
      id: 'notificationEntityGroupName',
      label: 'Name',
      alignment: CellAlignment.LEFT,
      size: CellSize.MEDIUM,
    },
  ];

  const {
    crossShippingGroups,
    hasMoreCrossShippingGroups,
    fetchCrossShippingGroups,
    isFetchingCrossShippingGroups,
  } = usePlantCrossShippingGroupApi({
    organizationId,
  });

  const renderCell = useCallback((rowData: any, cellId: string) => {
    return <div style={{ whiteSpace: 'nowrap' }}>{rowData[cellId]}</div>;
  }, []);

  useEffect(() => {
    if (crossShippingGroups.length || searchQuery) {
      fetchCrossShippingGroups({
        query: searchQuery,
        take: 50,
        page: 0,
        entityType: NotificationEntityType.PLANT,
      });
    }
  }, [fetchCrossShippingGroups, searchQuery]);

  const loadMoreHandler = useCallback(() => {
    fetchCrossShippingGroups({
      query: searchQuery,
      take: 50,
      page: Math.floor(crossShippingGroups.length / 50),
      entityType: NotificationEntityType.PLANT,
    });
  }, [fetchCrossShippingGroups, searchQuery, crossShippingGroups]);

  const changeSelectedValuesHandler = useCallback(
    (values: string[]) => {
      const currentItems = values.map((idWithPrefix: string) => {
        const prefix = idWithPrefix[0];
        const id = idWithPrefix.slice(1);
        const element = crossShippingGroups.find(
          el => el.notificationEntityGroupId.toString() === id,
        );
        const selectedElement = selectedItems?.find(
          el => el.id.toString() === id,
        );

        return element
          ? {
              id: element?.notificationEntityGroupId.toString() || '',
              label: element?.notificationEntityGroupName || '',
              prefix,
            }
          : { id: '', label: '', ...selectedElement, prefix };
      });

      onChangeSelectedEntities(values, currentItems);
    },
    [onChangeSelectedEntities, crossShippingGroups, selectedItems],
  );

  return (
    <div className={classes.selectContainer}>
      <CustomTypography variant="caption1" color="greyscale1">
        {label}
      </CustomTypography>

      <Select.Search
        searchQuery={searchQuery}
        isLoading={isFetchingCrossShippingGroups}
        valueId={'notificationEntityGroupId'}
        labelId={'notificationEntityGroupName'}
        options={crossShippingGroups}
        selectedValues={selectedValues?.map(el => {
          const item = selectedItems?.find(i => el.includes(i?.label));
          return el.split(item?.label ?? '').join(item?.id);
        })}
        selectedItems={selectedItems}
        onChangeSelectedValues={changeSelectedValuesHandler}
        onSearchHandler={value => {
          setSearchQuery(value);
        }}
        loadMoreHandler={loadMoreHandler}
        showLoader={hasMoreCrossShippingGroups}
        popoverSize={PopoverSize.BIG}
        renderCell={renderCell}
        columns={columns}
        clickHandler={() => {
          fetchCrossShippingGroups({
            query: searchQuery,
            take: 50,
            page: 0,
            entityType: NotificationEntityType.PLANT,
          });
        }}
      />
    </div>
  );
};
